import { z } from 'zod';

const bankAccountNumberRegex = /^[0-9A-Za-z]{5,17}$/;

export const BankAccountNumber = z
  .string()
  .min(1, {
    error: 'Required',
  })
  .min(5, {
    error: 'Must be at least 5 characters',
  })
  .max(17, {
    error: 'Must be at most 17 characters',
  })
  .regex(bankAccountNumberRegex, {
    error: 'Must be between 5 and 17 alphanumeric characters',
  });
export type BankAccountNumber = z.infer<typeof BankAccountNumber>;

/**
 * Validate a routing number's check digit according to the ABA RTN check digit algorithm
 *
 * Per https://en.wikipedia.org/wiki/ABA_routing_transit_number#Check_digit
 *
 *     The ninth, check digit provides a checksum test using a position-weighted sum of each of the digits. [...]
 *     The following condition must hold:
 *
 *     (3(d1 + d4 + d7) + 7(d2 + d5 + d8) + (d3 + d6 + d9)) mod 10 = 0
 *
 * @param val - The routing number to validate
 * @returns Whether the routing number is valid (true or false)
 */
export const isRoutingNumberCheckDigitValid = (val: string): boolean => {
  // checking length here is possibly redundant when combined with other length validations but seems sensible since this
  // is exported (for testing) and somebody else could try to use it without also checking the length first.
  if (val.length !== 9) {
    return false;
  }

  const digits = val.split('').map((d) => +d);
  return (
    (3 * (digits[0] + digits[3] + digits[6]) +
      7 * (digits[1] + digits[4] + digits[7]) +
      1 * (digits[2] + digits[5] + digits[8])) %
      10 ===
    0
  );
};

export const BankRoutingNumber = z
  .string()
  .min(1, {
    error: 'Required',
  })
  .length(9, {
    error: 'Must be 9 digits',
  })
  .refine(isRoutingNumberCheckDigitValid, {
    error: 'Invalid routing number',
  });
export type BankRoutingNumber = z.infer<typeof BankRoutingNumber>;

export const BankAccountType = z.enum(['checking', 'savings']);
export type BankAccountType = z.infer<typeof BankAccountType>;

export const ACH_RETURN_CODES: Record<string, string> = {
  R01: 'Insufficient Funds',
  R02: 'Account Closed',
  R03: 'No Account/Unable to Locate Account',
  R04: 'Invalid Account Number',
  R05: 'Unauthorized Debit to Consumer Account Using Corporate SEC Code',
  R06: 'ODFI Requested Return',
  R07: 'Authorization Revoked by Customer',
  R08: 'Payment Stopped',
  R09: 'Uncollected Funds',
  R10: 'Customer Advises Not Authorized',
  R11: 'Check Truncation Entry Return',
  R12: 'Branch Sold to Another DFI',
  R13: 'RDFI Not Qualified to Participate',
  R14: 'Representative Payee Deceased or Unable to Continue in That Capacity',
  R15: 'Beneficiary or Account Holder Deceased',
  R16: 'Account Frozen',
  R17: 'File Record Edit Criteria',
  R18: 'Improper Effective Entry Date',
  R19: 'Amount Field Error',
  R20: 'Non-Transaction Account',
  R21: 'Invalid Company Identification',
  R22: 'Invalid Individual ID Number',
  R23: 'Credit Entry Refused by Receiver',
  R24: 'Duplicate Entry',
  R25: 'Addenda Error',
  R26: 'Mandatory Field Error',
  R27: 'Trace Number Error',
  R28: 'Routing Number Check Digit Error',
  R29: 'Corporate Customer Advises Not Authorized',
  R30: 'RDFI Not Participant in Check Truncation Program',
  R31: 'Permissible Return Entry',
  R32: 'RDFI Non-Settlement',
  R33: 'Return of XCK Entry',
  R34: 'Limited Participation DFI',
  R35: 'Return of Improper Debit Entry',
  R36: 'Return of Improper Credit Entry',
  R37: 'Source Document Presented for Payment',
  R38: 'Stop Payment on Source Document',
  R39: 'Improper Source Document',
  R40: 'Return of ENR Entry by Federal Government Agency',
  R41: 'Invalid Transaction Code',
  R42: 'Routing Number/Check Digit Error',
  R43: 'Invalid DFI Account Number',
  R44: 'Invalid Individual ID Number/Identification Number',
  R45: 'Invalid Individual Name/Company Name',
  R46: 'Invalid Representative Payee Indicator',
  R47: 'Duplicate Enrollment',
  R50: 'State Law Affecting RCK Acceptance',
  R51: 'Item Related to RCK Entry is Ineligible',
  R52: 'Stop Payment on Item Related to RCK Entry',
  R53: 'Item and RCK Entry Presented for Payment',
  R61: 'Misrouted Return',
  R62: 'Return of Erroneous or Reversing Debit',
  R67: 'Duplicate Return',
  R68: 'Untimely Return',
  R69: 'Field Error(s)',
  R70: 'Permissible Return Entry Not Accepted/Return Not Requested by ODFI',
  R71: 'Misrouted Dishonored Return',
  R72: 'Untimely Dishonored Return',
  R73: 'Timely Original Return',
  R74: 'Corrected Return',
  R75: 'Return Not a Duplicate',
  R76: 'No Errors Found',
  R77: 'Non-Acceptance of R62 Dishonored Return',
  R80: 'IAT Entry Coding Error',
  R81: 'Non-Participant in IAT Program',
  R82: 'Invalid Foreign Receiving DFI Identification',
  R83: 'Foreign Receiving DFI Unable to Settle',
  R84: 'Entry Not Processed by Gateway',
  R85: 'Incorrectly Coded Outbound International Payment',
};

export const AchReturnCode = z
  .string()
  .min(3, {
    error: 'Must be at least 3 characters',
  })
  .toUpperCase()
  .refine((val) => ACH_RETURN_CODES[val] != null, {
    error: 'Invalid ach return code',
  });

export type AchReturnCode = z.infer<typeof AchReturnCode>;
